import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { submitContactForm } from '@/lib/api/contact';

const schema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Enter a valid email address'),
  subject: z
    .string()
    .min(5, 'Subject must be at least 5 characters')
    .max(100, 'Subject must be 100 characters or fewer'),
  message: z
    .string()
    .min(20, 'Message must be at least 20 characters')
    .max(2000, 'Message must be 2000 characters or fewer'),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ContactFormDialog({ open, onOpenChange }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const { mutate, isPending } = useMutation({
    mutationFn: (values: FormValues) => submitContactForm(values),
    onSuccess: () => {
      onOpenChange(false);
      reset();
      toast.success("Message sent! We'll get back to you soon.");
    },
    onError: () => {
      toast.error('Something went wrong. Please try again.');
    },
  });

  function onSubmit(values: FormValues) {
    mutate(values);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isPending) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Contact us</DialogTitle>
          <DialogDescription>
            Send a message to the CPN team. We typically respond within a few business days.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-2 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">
                Name <span aria-hidden="true">*</span>
              </Label>
              <Input
                id="contact-name"
                {...register('name')}
                placeholder="Your name"
                aria-describedby={errors.name ? 'contact-name-error' : undefined}
              />
              {errors.name && (
                <p id="contact-name-error" className="text-xs text-red-500" aria-live="polite">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact-email">
                Email <span aria-hidden="true">*</span>
              </Label>
              <Input
                id="contact-email"
                type="email"
                {...register('email')}
                placeholder="you@example.com"
                aria-describedby={errors.email ? 'contact-email-error' : undefined}
              />
              {errors.email && (
                <p id="contact-email-error" className="text-xs text-red-500" aria-live="polite">
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact-subject">
              Subject <span aria-hidden="true">*</span>
            </Label>
            <Input
              id="contact-subject"
              {...register('subject')}
              placeholder="What's this about?"
              aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
            />
            {errors.subject && (
              <p id="contact-subject-error" className="text-xs text-red-500" aria-live="polite">
                {errors.subject.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact-message">
              Message <span aria-hidden="true">*</span>
            </Label>
            <Textarea
              id="contact-message"
              {...register('message')}
              placeholder="Tell us how we can help…"
              className="min-h-[120px] resize-none border-muted/30 text-sm"
              maxLength={2000}
              aria-describedby={errors.message ? 'contact-message-error' : undefined}
            />
            {errors.message && (
              <p id="contact-message-error" className="text-xs text-red-500" aria-live="polite">
                {errors.message.message}
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 pt-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-navy text-cream hover:bg-navy-deep"
              disabled={isPending}
            >
              {isPending ? 'Sending…' : 'Send message'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
